import { logActivity } from '@/lib/activity-log';
import { getFiles } from '@/lib/api/services/storage.service';
import { graphqlClient } from '@/lib/graphql-client';
import { mutateGateway } from './gateway-mutate';
import { getUserScope } from '@/lib/user-scope';
import { getOrganizationTimeZone } from '@/lib/utils/custom-date/org-time-zone';
import { useAuthStore } from '@/state/store/auth';
import {
  INSERT_ACTION_LOG_MUTATION,
  INSERT_HR_PROFILE_MUTATION,
  INSERT_USER_CONTACT_DETAIL_MUTATION,
  INSERT_USER_PROFILE_DETAIL_MUTATION,
  UPDATE_HR_PROFILE_MUTATION,
  UPDATE_USER_CONTACT_DETAIL_MUTATION,
  UPDATE_USER_PROFILE_DETAIL_MUTATION,
} from '../graphql/mutations';
import {
  buildGetContactDetailsQuery,
  buildGetDesignationsLookupQuery,
  buildGetEmployeesQuery,
  buildGetHrProfilesQuery,
} from '../graphql/queries';
import { employeeName, toDisplayName } from '../schemas/display-name';
import { WorkStatusChanges } from '../schemas/work-status';
import {
  Employee,
  EmployeeCreateInput,
  EmployeesPagingResult,
  GetEmployeesParams,
  HrTermInput,
  LookupResult,
  OrganizationRoleAssignment,
} from '../types/employee.types';
import {
  deactivateIamUser,
  getIamUserActive,
  getIamUserSummary,
  IamProfileDetails,
  isOrganizationAccessReduced,
  provisionLoginUser,
  reactivateIamUser,
  revokeIamUserSessions,
  syncIamUserDetails,
  syncIamUserEmail,
  syncOrganizationAccess,
  updateMyIamProfile,
} from './employee-account.service';
import {
  ContactDetailRow,
  currentWorkDaysOf,
  EmployeeScope,
  HrProfileRow,
  scopeOf,
  splitProfileInput,
  toEmployee,
  toHrProfileInput,
  toScopeInput,
} from './employee-mappers';

/**
 * Active state of the person's Blocks OS login. Someone without a login user cannot sign in, so counts as
 * inactive; a viewer without IAM read access gets `undefined` (unknown) rather than a failed page.
 */
export const getLoginActive = (userId?: string): Promise<boolean | undefined> =>
  userId ? getIamUserActive(userId).catch(() => undefined) : Promise.resolve(false);

/**
 * One page of the directory. IAM keeps whether each login is active and has no way to read several users
 * at once, so the list does not wait for it: the page asks per row (see `useEmployeesLoginActive`).
 */
export const getEmployees = async (
  params: GetEmployeesParams
): Promise<{ Employees: EmployeesPagingResult }> => {
  const { pageNo, pageSize, filter = {}, order } = params;
  return graphqlClient.query<{ Employees: EmployeesPagingResult }>({
    query: buildGetEmployeesQuery(filter, pageNo, pageSize, order),
  });
};

const HR_PROFILE_PAGE_SIZE = 100;

/** Every HR term of one employee, oldest first. All terms fit in one page, so they are sorted
 * client-side by `HRPeriod` ascending, matching the old `{"HRPeriod":1}` sort. */
const getHrProfiles = async (userId: string): Promise<HrProfileRow[]> => {
  const result = await graphqlClient.query<{ HrProfiles: { items: HrProfileRow[] } }>({
    query: buildGetHrProfilesQuery({ UserId: userId }, 1, HR_PROFILE_PAGE_SIZE),
  });
  return [...(result.HrProfiles.items ?? [])].sort((a, b) => (a.HRPeriod ?? 0) - (b.HRPeriod ?? 0));
};

/** The employee's work days today in their current HR term; empty when none is on file. */
export const getEmployeeCurrentWorkDays = async (userId: string): Promise<string[]> =>
  userId ? currentWorkDaysOf(await getHrProfiles(userId)) : [];

/** The person's `UserContactDetail` (one per user), or null while none was saved. */
const getContactDetail = async (userId: string): Promise<ContactDetailRow | null> => {
  const result = await graphqlClient.query<{ ContactDetails: { items: ContactDetailRow[] } }>({
    query: buildGetContactDetailsQuery({ UserId: userId }, 1, 1),
  });
  return result.ContactDetails.items?.[0] ?? null;
};

const loadEmployee = async (
  match: Record<string, string>
): Promise<{ Employees: EmployeesPagingResult }> => {
  const result = await graphqlClient.query<{ Employees: EmployeesPagingResult }>({
    query: buildGetEmployeesQuery(match, 1, 1),
  });
  const profile = result.Employees.items[0];
  if (!profile) return result;

  const userId = profile.UserId ?? '';
  // The login (active state, organization access) is display/prefill data, read once: a viewer without
  // IAM read access still gets the profile, with the active state unknown. No login user: inactive.
  const [hrProfiles, login, contact] = await Promise.all([
    userId ? getHrProfiles(userId) : Promise.resolve([]),
    userId
      ? getIamUserSummary(userId).catch(() => ({ isActive: undefined, organizationAccess: [] }))
      : Promise.resolve({ isActive: false, organizationAccess: [] }),
    userId ? getContactDetail(userId) : Promise.resolve(null),
  ]);
  const employee = {
    ...toEmployee(profile, hrProfiles, login, contact),
    IsActive: login.isActive,
  };
  return { Employees: { ...result.Employees, items: [employee] } };
};

/** Basic info + contact details + HR terms + IAM roles, merged into the `Employee` view model. */
export const getEmployeeById = async (
  itemId: string
): Promise<{ Employees: EmployeesPagingResult }> => loadEmployee({ ItemId: itemId });

/** The employee record of a Blocks OS IAM user (their `UserProfileDetail` is linked by `UserId`). */
export const getEmployeeByUserId = async (
  userId: string
): Promise<{ Employees: EmployeesPagingResult }> => loadEmployee({ UserId: userId });

export const getDesignationsLookup = async (): Promise<{ Designations: LookupResult }> => {
  return graphqlClient.query({
    query: buildGetDesignationsLookupQuery({}, 1, 300),
  });
};

/** The `UserProfileDetail` linked to an IAM user, or null while there is none. */
const findProfileOfUser = async (userId: string): Promise<Employee | null> => {
  const result = await graphqlClient.query<{ Employees: EmployeesPagingResult }>({
    query: buildGetEmployeesQuery({ UserId: userId }, 1, 1),
  });
  return result.Employees.items?.[0] ?? null;
};

const isProfileLinkedToUser = async (userId: string): Promise<boolean> =>
  Boolean(await findProfileOfUser(userId));

/**
 * Whether an IAM user already belongs to a fully added person. A profile still without its contact
 * detail is the leftover of an "Add employee" that failed half-way, which the next attempt finishes.
 */
const isUserAddedCompletely = async (userId: string): Promise<boolean> =>
  Boolean((await findProfileOfUser(userId)) && (await getContactDetail(userId)));

const updateProfile = (itemId: string, input: Record<string, unknown>) =>
  mutateGateway({
    query: UPDATE_USER_PROFILE_DETAIL_MUTATION,
    variables: { filter: JSON.stringify({ _id: itemId }), input },
  });

const text = (value: unknown) => (typeof value === 'string' ? value : undefined);

/**
 * The name, phone and photo the person's IAM user must carry for a saved profile. IAM stores the photo
 * as a URL while the profile keeps the storage file id, so the URL is only looked up when the photo
 * changed (`saved` is the profile as it was before this save). A field `source` does not carry is left
 * as IAM has it.
 */
const toIamDetails = async (source: object, saved?: Employee): Promise<IamProfileDetails> => {
  const fields = source as Record<string, unknown>;
  const imageId = text(fields.ProfileImageId);
  const photoChanged = imageId !== undefined && imageId !== (saved?.ProfileImageId ?? '');
  const photoUrl = photoChanged
    ? imageId
      ? (await getFiles([imageId])).data?.[0]?.url || undefined
      : ''
    : undefined;

  return {
    firstName: text(fields.FirstName),
    lastName: text(fields.LastName),
    phoneNumber: text(fields.PhoneNumber),
    ...(photoUrl !== undefined ? { profileImageUrl: photoUrl } : {}),
  };
};

/**
 * Profiles created before login users existed get their OS user the first time HR saves them, so they
 * can sign in. Roles are assigned separately from the directory list.
 */
const ensureLoginUser = async (employee: Employee): Promise<string> => {
  if (employee.UserId) return employee.UserId;
  const { userId } = await provisionLoginUser(
    {
      email: employee.Email,
      firstName: employee.FirstName,
      lastName: employee.LastName,
      phoneNumber: employee.PhoneNumber,
      roles: [],
    },
    isProfileLinkedToUser
  );
  await updateProfile(employee.ItemId, { UserId: userId });
  return userId;
};

const SCOPE_KEYS = ['UserId', 'DepartmentId', 'DepartmentName', 'DepartmentKey'];

const omitScope = (fields: Record<string, unknown>) =>
  Object.fromEntries(Object.entries(fields).filter(([key]) => !SCOPE_KEYS.includes(key)));

/**
 * Inserts or updates the person's `UserContactDetail`, stamped with their current scope. A person's own
 * save passes `rescope: false`: the access rules let only HR write the scope fields of an existing row,
 * so their update leaves them out (a new row is still stamped).
 */
const upsertContactDetail = async (
  contactDetailItemId: string | undefined,
  scope: EmployeeScope,
  fields: Record<string, unknown>,
  { rescope = true }: { rescope?: boolean } = {}
) => {
  const input = { ...fields, ...toScopeInput(scope) };
  if (contactDetailItemId) {
    return mutateGateway({
      query: UPDATE_USER_CONTACT_DETAIL_MUTATION,
      variables: {
        filter: JSON.stringify({ _id: contactDetailItemId }),
        input: rescope ? input : omitScope(fields),
      },
    });
  }
  return mutateGateway({
    query: INSERT_USER_CONTACT_DETAIL_MUTATION,
    variables: { input },
  });
};

/**
 * Adds a person with the minimum HR enters (like adding a user in Blocks OS): the login user (which mails
 * the activation link), their basic info and an empty contact detail. HR terms, the rest of the profile
 * and roles are filled in afterwards. Nothing is rolled back when a step fails (only a super admin may
 * delete a profile): the IAM user and a profile saved without its contact detail are picked up and
 * finished by the next attempt with the same email.
 *
 * The login user is provisioned into the creator's current organization (not the IAM default). The new
 * profile starts incomplete only when the creator's organization requires extended info
 * (`requiresExtendedInfo`); the person is then sent to "Edit profile" on their first sign-in.
 */
export const createEmployee = async (
  input: EmployeeCreateInput,
  {
    requiresExtendedInfo,
    organizationId,
  }: { requiresExtendedInfo: boolean; organizationId?: string }
): Promise<{ profileId: string }> => {
  const { userId, isNew } = await provisionLoginUser(
    {
      email: input.Email,
      firstName: input.FirstName,
      lastName: input.LastName,
      phoneNumber: input.PhoneNumber,
      roles: [],
      ...(organizationId ? { organizationId } : {}),
    },
    isUserAddedCompletely
  );
  // A reused IAM user (an orphan, or someone with a login but no profile yet) may carry an older name.
  // HR adding themselves goes through their own account (`iam/me`), like every other save of their own.
  if (!isNew) {
    const details = await toIamDetails(input);
    if (userId === useAuthStore.getState().user?.itemId) await updateMyIamProfile(details);
    else await syncIamUserDetails(userId, details);
  }

  const profileInput = {
    // Everybody works from home until HR picks office days for them.
    IsWFH: true,
    ...input,
    UserId: userId,
    // The person completes the rest themselves on first sign-in, see `useProfileCompletion`.
    IsProfileCompleted: !requiresExtendedInfo,
  };
  const leftover = await findProfileOfUser(userId);
  let profileId: string;
  if (leftover) {
    await updateProfile(leftover.ItemId, profileInput);
    profileId = leftover.ItemId;
  } else {
    const profile = await mutateGateway<{ insertUserProfileDetail: { itemId: string } }>({
      query: INSERT_USER_PROFILE_DETAIL_MUTATION,
      variables: { input: profileInput },
    });
    profileId = profile.insertUserProfileDetail.itemId;
  }

  await upsertContactDetail(
    undefined,
    {
      userId,
      departmentId: input.DepartmentId,
      departmentName: input.DepartmentName,
      departmentKey: input.DepartmentKey,
    },
    { IsPrivateInfoVisible: false }
  );
  return { profileId };
};

const DEPARTMENT_KEYS = ['DepartmentId', 'DepartmentName', 'DepartmentKey'] as const;

const unchanged = (before: Employee, after: Employee, keys: readonly (keyof Employee)[]) =>
  keys.every((key) => (before[key] ?? '') === (after[key] ?? ''));

/**
 * The department is denormalised onto the contact detail and every HR term (their access rules read it
 * there), so a department change on the basic info follows to those rows.
 */
const propagateScope = async (before: Employee, after: Employee) => {
  if (unchanged(before, after, DEPARTMENT_KEYS)) return;
  const scope = scopeOf(after);
  await Promise.all([
    ...(after.ContactDetailItemId
      ? [upsertContactDetail(after.ContactDetailItemId, scope, {})]
      : []),
    ...after.HrTerms.map((term) =>
      mutateGateway({
        query: UPDATE_HR_PROFILE_MUTATION,
        variables: { filter: JSON.stringify({ _id: term.ItemId }), input: toScopeInput(scope) },
      })
    ),
  ]);
};

/**
 * HR saves the "Overview" tab: the person's `UserProfileDetail` (basic info). Any `UserContactDetail`
 * field in the input (the contact tab owns them) still goes to that record.
 */
export const updateEmployeeBasicInfo = async (params: {
  employee: Employee;
  input: Record<string, unknown>;
}) => {
  const { employee, input } = params;
  const { profile, contact } = splitProfileInput(input);
  const userId = await ensureLoginUser(employee);
  // A changed name or photo must reach the OS login user before the profile is saved with it.
  await syncIamUserDetails(userId, await toIamDetails(profile, employee));

  const saved = await updateProfile(employee.ItemId, profile);
  if (Object.keys(contact).length) {
    await upsertContactDetail(
      employee.ContactDetailItemId,
      { ...scopeOf(employee), userId },
      contact
    );
  }
  await propagateScope(employee, { ...employee, ...profile, UserId: userId } as Employee);
  return saved;
};

/** The business email / phone live on the `UserProfileDetail`; everything else on the `UserContactDetail`. */
const splitContactInput = (input: Record<string, unknown>) => {
  const { Email, PhoneNumber, IsProfileCompleted: _isProfileCompleted, ...contact } = input;
  return { business: { Email, PhoneNumber }, contact };
};

/** HR saves the "Contact Info" tab. */
export const updateEmployeeContactInfo = async (params: {
  employee: Employee;
  input: Record<string, unknown>;
}) => {
  const { employee, input } = params;
  const { business, contact } = splitContactInput(input);
  const userId = await ensureLoginUser(employee);

  // A changed email or phone must reach the OS login user before the profile is saved with it.
  if (typeof business.Email === 'string') await syncIamUserEmail(userId, business.Email);
  await syncIamUserDetails(userId, await toIamDetails({ PhoneNumber: business.PhoneNumber }));
  await updateProfile(employee.ItemId, business);

  return upsertContactDetail(
    employee.ContactDetailItemId,
    { ...scopeOf(employee), userId },
    contact
  );
};

/** Inserts a new HR term and returns its id. */
const insertHrProfile = async (term: HrTermInput, scope: EmployeeScope) => {
  const result = await mutateGateway<{
    insertUserHRPeriodicalProfile: { itemId: string };
  }>({
    query: INSERT_HR_PROFILE_MUTATION,
    variables: { input: toHrProfileInput(term, scope) },
  });
  return result.insertUserHRPeriodicalProfile.itemId;
};

/**
 * HR saves the "HR Info" tab: one `UserHRPeriodicalProfile` per HR term (period and carry-forwards), with
 * its working days and leave allotment embedded. Terms that already exist are updated in place (they
 * can never be removed), new terms are inserted one after another so they are stored in period order.
 * Returns the id of every term in the order given, so the form can keep the ids of terms it just inserted;
 * a term sent without an id whose period is already saved updates that row instead of duplicating it.
 */
export const updateEmployeeHrInfo = async (params: {
  employee: Employee;
  terms: HrTermInput[];
}): Promise<string[]> => {
  const { employee, terms } = params;
  const userId = await ensureLoginUser(employee);
  const scope = { ...scopeOf(employee), userId };
  const savedIdByPeriod = new Map(employee.HrTerms.map((term) => [term.HRPeriod, term.ItemId]));
  const termIds: string[] = [];

  for (const term of terms) {
    let termId = term.ItemId || savedIdByPeriod.get(term.HRPeriod);
    if (termId) {
      await mutateGateway({
        query: UPDATE_HR_PROFILE_MUTATION,
        variables: {
          filter: JSON.stringify({ _id: termId }),
          input: toHrProfileInput(term, scope),
        },
      });
    } else {
      termId = await insertHrProfile(term, scope);
    }
    termIds.push(termId);
  }
  return termIds;
};

/** The "Assign role" dialog: the organizations the person's login user has access to, and the roles in each. */
export const updateEmployeeRoles = async (params: {
  employee: Employee;
  organizationAccess: OrganizationRoleAssignment[];
}) => {
  const { employee, organizationAccess } = params;
  const userId = await ensureLoginUser(employee);
  // A profile without a prior login user has no previous organization access to diff against.
  const before = employee.UserId ? (employee.OrganizationAccess ?? []) : [];
  const beforeAccess = before.map((organization) => ({
    organizationId: organization.OrganizationId,
    roles: organization.RoleKeys,
  }));
  const afterAccess = organizationAccess.map((organization) => ({
    organizationId: organization.OrganizationId,
    roles: organization.RoleKeys,
  }));
  await syncOrganizationAccess(userId, beforeAccess, afterAccess);
  // Removed access must not live on in the user's open sessions: sign them out everywhere.
  if (isOrganizationAccessReduced(beforeAccess, afterAccess)) await revokeIamUserSessions(userId);
};

/**
 * Saves the person's own `UserProfileDetail` (and the personal details kept on their
 * `UserContactDetail`): never HR terms, roles or the fields HR owns. Like an HR save, a changed
 * department follows to the contact detail and every HR term. The IAM user is not touched here; see
 * {@link updateMyBasicInfo}.
 */
export const saveMyProfileDetail = async (params: {
  employee: Employee;
  input: Record<string, unknown>;
}) => {
  const { employee, input } = params;
  const { profile, contact } = splitProfileInput(input);
  const saved = await updateProfile(employee.ItemId, profile);
  if (Object.keys(contact).length) {
    await upsertContactDetail(employee.ContactDetailItemId, scopeOf(employee), contact, {
      rescope: false,
    });
  }
  await propagateScope(employee, { ...employee, ...profile } as Employee);
  return saved;
};

/**
 * A person saving their own "Overview" tab: the login user goes first (name and photo), like the email
 * on an HR edit, so if IAM refuses nothing is saved and the two stay in step.
 */
export const updateMyBasicInfo = async (params: {
  employee: Employee;
  input: Record<string, unknown>;
}) => {
  await updateMyIamProfile(await toIamDetails(params.input, params.employee));
  return saveMyProfileDetail(params);
};

/**
 * A person saving their own "Contact Info" tab; the login user's phone follows. The business email is
 * their login, which only HR changes, so it is never saved from here.
 */
export const updateMyContactInfo = async (params: {
  employee: Employee;
  input: Record<string, unknown>;
}) => {
  const { employee, input } = params;
  const { business, contact } = splitContactInput(input);
  await updateMyIamProfile(await toIamDetails({ PhoneNumber: business.PhoneNumber }));
  await updateProfile(employee.ItemId, {
    PhoneNumber: business.PhoneNumber,
    ...pickCompletion(input),
  });
  return upsertContactDetail(employee.ContactDetailItemId, scopeOf(employee), contact, {
    rescope: false,
  });
};

/** Name, phone and photo the person changes about themselves from the profile page's account dialog. */
export interface MyAccountDetails {
  FirstName: string;
  LastName: string;
  PhoneNumber: string;
  /** The photo's storage file id (kept on the profile) and URL (kept on IAM); both left out while unchanged, `''` clears it. */
  ProfileImageId?: string;
  ProfileImageUrl?: string;
}

/**
 * The profile page's account dialog. Same order as the "Overview" tab: the login user goes first, so if
 * IAM refuses nothing is saved; then the person's own `UserProfileDetail` (when they have one) follows.
 */
export const updateMyAccountDetails = async (params: {
  employee?: Employee;
  details: MyAccountDetails;
}) => {
  const { employee, details } = params;
  await updateMyIamProfile({
    firstName: details.FirstName,
    lastName: details.LastName,
    phoneNumber: details.PhoneNumber,
    ...(details.ProfileImageUrl !== undefined ? { profileImageUrl: details.ProfileImageUrl } : {}),
  });
  if (!employee) return;
  await saveMyProfileDetail({
    employee,
    input: {
      FirstName: details.FirstName,
      LastName: details.LastName,
      DisplayName: toDisplayName(details.FirstName, details.LastName),
      PhoneNumber: details.PhoneNumber,
      ...(details.ProfileImageId !== undefined ? { ProfileImageId: details.ProfileImageId } : {}),
    },
  });
};

/** `IsProfileCompleted` rides along with whichever self-service tab is saved. */
const pickCompletion = (input: Record<string, unknown>) =>
  typeof input.IsProfileCompleted === 'boolean'
    ? { IsProfileCompleted: input.IsProfileCompleted }
    : {};

/**
 * Deactivates or reactivates a person's Blocks OS login (like the legacy backend's `toggleUserStatus`).
 * IAM is the only record of the state. A profile with no login user yet has nothing to deactivate;
 * reactivating it creates the login user, which mails the activation link.
 */
export const setEmployeeActive = async (params: { employee: Employee; isActive: boolean }) => {
  const { employee, isActive } = params;
  if (!isActive) {
    if (employee.UserId) await deactivateIamUser(employee.UserId);
    return;
  }
  if (employee.UserId) await reactivateIamUser(employee.UserId);
  else await ensureLoginUser(employee);
};

const onOff = (value: boolean) => (value ? 'ON' : 'OFF');

/** One human-readable line per switch that changed, e.g. `WFH status to ON` (audit text, not UI copy). */
export const describeWorkStatusChange = (changes: WorkStatusChanges): string => {
  const parts: string[] = [];
  if (changes.IsWFH !== undefined) parts.push(`WFH status to ${onOff(changes.IsWFH)}`);
  if (changes.WFODays?.length && changes.IsWFH !== true) {
    parts.push(`office days to ${changes.WFODays.join(', ')}`);
  }
  return parts.join(' and ');
};

// Audit trail of who flipped a switch. Best effort: it must never fail the change that was just saved.
const recordWorkStatusChange = async (employee: Employee, changes: WorkStatusChanges) => {
  const actor = useAuthStore.getState().user;
  const actorName = `${actor?.firstName ?? ''} ${actor?.lastName ?? ''}`.trim() || 'Admin';
  const personName = employeeName(employee);
  const description = describeWorkStatusChange(changes);

  await logActivity({
    moduleName: 'hr-directory',
    entityType: 'UserProfileDetail',
    entityId: employee.ItemId,
    action: 'Update',
    summary: `${actorName} changed ${description} for ${personName}`,
    changes: { ...changes },
  });

  // The legacy per-employee action log for work from home, kept in every organization.
  if (changes.IsWFH === undefined) return;
  try {
    await mutateGateway({
      query: INSERT_ACTION_LOG_MUTATION,
      variables: {
        input: {
          ActionDateUtc: new Date().toISOString(),
          Timezone: getOrganizationTimeZone(),
          Action: `${actorName} changed WFH status to ${onOff(changes.IsWFH)} for ${personName}`,
          Type: 'WFH',
          UserId: employee.UserId ?? '',
          DisplayName: personName,
          ActorUserId: actor?.itemId ?? '',
          ActorDisplayName: actorName,
          ReferenceId: employee.ItemId,
          ...(await getUserScope(employee.UserId ?? '')),
        },
      },
    });
  } catch (error) {
    console.warn('[hr-directory] failed to record the WFH action log entry', error);
  }
};

/**
 * Saves work from home and the office days for one employee (legacy `updateUserWFHStatus`). Only the
 * fields in `changes` are written. Lunch and overtime are role permissions now, see `work-status.ts`.
 */
export const updateEmployeeWorkStatus = async (params: {
  employee: Employee;
  changes: WorkStatusChanges;
}) => {
  const { employee, changes } = params;
  const result = await mutateGateway({
    query: UPDATE_USER_PROFILE_DETAIL_MUTATION,
    variables: { filter: JSON.stringify({ _id: employee.ItemId }), input: changes },
  });
  await recordWorkStatusChange(employee, changes);
  return result;
};
